/* Wizard · Pickers (large chunky card pickers used in Step 1 Case Setup)
 * Extracted iter101.91 from `/app/frontend/src/pages/doctor/CaseSubmissionPage.jsx`.
 *
 * Contents:
 *   · PlanningModePicker         — Planning mode (Invisique™ vs Self)
 *   · ModeCard                   — Shared "chunky card" primitive
 *   · CoeSupportCompliancePicker — Compliance monitoring toggle
 *   · ScanSourcePicker           — Scan source (IOS / impressions / etc.)
 *   · RadioRailRow               — Inline radio-rail row primitive
 */
import React, { useState, useMemo } from "react";
import { useSettlePulse } from "./hooks";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger } from "@/components/ui/dialog";
import { toast } from "sonner";
import api from "@/lib/api";
import { API } from "@/lib/api";
import {
    CaretRight, CaretLeft, CaretDown, CheckCircle, Circle, Upload, X, Check,
    MagnifyingGlass, ArrowsCounterClockwise, Crown, FileArrowUp, GitBranch,
    Warning, Lightning, Clock, Info, Tooth, Star, ShieldCheck, Cube, Cloud,
    CloudArrowUp, File as FileIcon, FilePdf, FileZip, FileVideo, Sparkle, Tag,
    ClipboardText, HeartStraight, Ruler, TrendUp, Sliders, Package, Prohibit,
    Notepad, ListBullets, Pencil, ArrowSquareUp, ArrowSquareDown, CircleHalf,
    Printer, BookmarkSimple, FloppyDisk, Trash, ChartBar, Hourglass, Calculator,
    Lock, UserGear, Camera, Stethoscope, HouseLine, Plus, Prescription,
    Headset, Truck, Receipt, SealCheck, Buildings, Hash, Scan, ArrowRight,
    User as UserIcon,
} from "@phosphor-icons/react";
import {
    SelectedPill, Step1SectionHeader, cardBadgeClass, resolveHeroIcon,
    fmtINR,
} from "@/components/case/wizard/shared";


export function ScanSourcePicker({ value, onChange, scannerProfile, ioScannerFee, onBookScanVisit, patientSelected = false, hideHeader = false, scanSourceCards = null, scanServiceHero = null }) {
    // Title + meta for the "Upload my own scans" card. If the doctor
    // declared a scanner on their profile (iter97.80) name-check it —
    // micro-personalisation pays off in trust + perceived intelligence
    // of the product.
    const declared = scannerProfile?.in_house_scanner === "yes";
    const brand = scannerProfile?.scanner_brand || "";
    const model = scannerProfile?.scanner_model || "";
    const scannerLabel = [brand, model].filter(Boolean).join(" ").trim();

    /* iter98.99q — Live admin-editable copy. When the admin sets a
     * custom title / subtitle in Pricing → Wizard Cards, the wizard
     * renders that instead of the auto-personalised default below.
     * Personalised fallback wins only when admin hasn't overridden. */
    const adminSelf = scanSourceCards?.self_capture || {};
    const adminIO   = scanSourceCards?.io_service   || {};

    const defaultUploadTitle = declared && scannerLabel
        ? `Self-capture · I'll upload from my ${scannerLabel}`
        : "Self-capture · I'll upload my own scans";
    const defaultUploadHint = declared
        ? "STL / PLY / OBJ exports — continue to Step 3 to attach files"
        : "STL scans + intraoral / extraoral photos from your clinic";

    const uploadTitle = (adminSelf.title || "").trim() || defaultUploadTitle;
    const uploadHint  = (adminSelf.subtitle || "").trim() || defaultUploadHint;

    /* iter100.108 — Scanner-device chip. When the admin sets a custom
     * `title`/`subtitle` for the self-capture card, those overrides win
     * (admin authority), which used to silently drop the doctor's
     * personalised "your TRIOS 4 is ready" line. To preserve the
     * personalisation regardless of admin copy, we always render a
     * small "Your scanner: ..." chip on the self-capture card whenever
     * the doctor has declared an in-house scanner on their profile. */
    const deviceChip = declared && scannerLabel
        ? { icon: Scan, label: `Your scanner: ${scannerLabel}` }
        : null;

    const defaultIoTitle = "IO Scans by Invisique™ · Trained tech visits your clinic";
    const defaultIoHint  = ioScannerFee != null
        ? `₹${Number(ioScannerFee).toLocaleString("en-IN")} per scan · non-refundable · captured + uploaded for you`
        : "Live pricing from Invisique™ · technician captures + uploads for you";

    const ioTitle = (adminIO.title || "").trim() || defaultIoTitle;
    const ioPriceHint = (adminIO.subtitle || "").trim() || defaultIoHint;

    /* iter98.99q — Optional ribbon badges set by the admin (Pricing →
     * Wizard Cards → Scan Source). We surface them as small floating
     * pills on each radio row so the admin's "EXCLUSIVE" / "POPULAR"
     * labels actually appear in the wizard.
     *
     * iter98.99s — Smart per-clinic nudge. When the doctor's profile
     * confirms they do NOT have an in-house scanner (either declared
     * "no" or simply hasn't declared one), we auto-recommend the IO
     * Scans path with a "RECOMMENDED FOR YOU" ribbon — only when the
     * admin hasn't already set an explicit IO badge (admin wins). For
     * doctors WITH a declared scanner we suggest self-capture as the
     * no-add-on default. The suggestion is purely visual — the doctor
     * still chooses freely. */
    const selfBadgeAdmin    = (adminSelf.badge || "").trim();
    const selfBadgeColor    = (adminSelf.badge_color || adminSelf.accent || "primary").trim();
    const ioBadgeAdmin      = (adminIO.badge || "").trim();
    const ioBadgeColor      = (adminIO.badge_color || adminIO.accent || "primary").trim();
    // Profile must be loaded before we nudge, so the badge doesn't
    // flicker in for a frame on the doctor's first render.
    const profileLoaded     = scannerProfile != null;
    const noScannerDeclared = profileLoaded && !declared;
    const selfBadge = selfBadgeAdmin || (declared ? "YOUR DEFAULT" : "");
    const ioBadge   = ioBadgeAdmin   || (noScannerDeclared ? "RECOMMENDED FOR YOU" : "");
    // Smart-recommended pills always use the brand-primary teal (matches
    // the section's hue) so the cue feels native to Section 01 instead
    // of a marketing chip. Admin's badge_color wins when admin set the
    // badge text.
    const selfBadgeColorFinal = selfBadgeAdmin ? selfBadgeColor : "primary";
    const ioBadgeColorFinal   = ioBadgeAdmin   ? ioBadgeColor   : "primary";

    /* iter97.92 — Inline visual radio rail.
       Two boxy cards stacked under the (already-boxy) Submission Mode
       felt repetitive. We replace them with a vertical "radio rail":
       a thin connector line on the left with two radio nodes — first
       solid (selected), second outline. Each option is a single row
       (title line + meta line). Hover lights the row in a primary tint
       so the affordance reads as clickable without the heavy card chrome.
       Total section height drops from ~330px to ~120px.
       iter98.99b — User asked for side-by-side radios. Switched from
       vertical rail to a 2-col grid; the shared vertical rail line is
       removed (the two cards now sit beside each other so the connector
       no longer makes visual sense). Labels also bumped 1pt up. */
    return (
        <div data-testid="scan-source-picker">
            {!hideHeader && (
                <div className="mb-2.5">
                    <span className="text-[11px] font-bold tracking-[0.18em] uppercase text-muted-foreground">Scan source</span>
                    <span className="text-[12px] text-muted-foreground ml-2">How will the scans for this case be captured?</span>
                </div>
            )}

            {/* iter98.99s — `pt-3` (was `gap-2`) gives the floating
                ribbon badges room to breathe between the section header
                above and the cards below. */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 pt-3">
                {/* Self-capture option — soft brand-primary tone */}
                <RadioRailRow
                    selected={value === "upload"}
                    onClick={() => onChange("upload")}
                    title={uploadTitle}
                    hint={uploadHint}
                    accent="primary"
                    tone="soft"
                    testid="scan-source-upload"
                    badge={selfBadge}
                    badgeColor={selfBadgeColorFinal}
                    bullets={Array.isArray(adminSelf.bullets) ? adminSelf.bullets : []}
                    deviceChip={deviceChip}
                />

                {/* Invisique™ IO Service option — stronger brand-primary tone */}
                <RadioRailRow
                    selected={value === "io_service"}
                    onClick={() => onChange("io_service")}
                    title={ioTitle}
                    hint={ioPriceHint}
                    accent="primary"
                    tone="bold"
                    testid="scan-source-io-service"
                    badge={ioBadge}
                    badgeColor={ioBadgeColorFinal}
                    bullets={Array.isArray(adminIO.bullets) ? adminIO.bullets : []}
                />
            </div>

            {/* iter97.83 → iter98.99ac → iter98.99g → iter98.99q — Premium
                "Book scan visit" CTA. Section 01 (Scan source) uses the
                brand Primary teal (`#007C9D`), so this panel inherits a
                brand-tinted gradient body. The CTA button shares the
                same brand-primary color — the whole section reads as
                one cohesive brand surface.

                iter98.99q — Every line of copy in this panel is admin-
                editable via Pricing → Wizard Cards → IO Scans hero.
                We resolve admin fields with hardcoded defaults so the
                UI never goes blank if a field is unset. */}
            {value === "io_service" && (() => {
                const hero = scanServiceHero || {};
                const heroEyebrow = (hero.eyebrow || "").trim() || "White-glove records";
                const heroEyebrowColor = (hero.badge_color || "").trim() || "primary";
                const heroTitle = (hero.title || "").trim() || "Get 45 minutes of chair-time back. Every case.";
                const heroSubtitle = (hero.subtitle || "").trim()
                    || "Even doctors with their own IO scanner book this — because a trained tech delivers consistent records, a DSLR series most clinics don't own, and you keep treating revenue patients while we capture.";
                const heroCtaLabel = (hero.cta_label || "").trim() || "Book scan visit";
                const heroPromises = Array.isArray(hero.promises) && hero.promises.length
                    ? hero.promises
                    : [
                        { icon: "Clock",        label: "Chair time back",  value: "45 min · every case",       sub: "See the next patient while our tech captures records." },
                        { icon: "Camera",       label: "Pro-grade DSLR",   value: "4-angle photo series",      sub: "Frontal · profile · occlusal · smile — the records most clinics can't self-capture." },
                        { icon: "CloudArrowUp", label: "Same-day upload",  value: "Records land before you do", sub: "Direct to your case. No emails, no USB sticks, no follow-ups." },
                    ];
                const heroFeatures = Array.isArray(hero.features) && hero.features.length
                    ? hero.features
                    : [
                        { label: "3D IO scan",         value: "— upper, lower & bite" },
                        { label: "DSLR clinical photos", value: "— frontal, profile, occlusal, smile" },
                        { label: "Bite registration",   value: "& extras" },
                        { label: "Free re-scan",       value: "if records aren't approved" },
                    ];
                return (
                <div
                    className="mt-3 relative overflow-hidden rounded-2xl border border-primary/20 shadow-sm bg-gradient-to-br from-primary/[0.06] via-white to-primary/[0.12]"
                    data-testid="scan-source-io-cta"
                >
                    {/* Decorative brand glow in the top-right corner */}
                    <div aria-hidden="true" className="pointer-events-none absolute -top-16 -right-16 w-48 h-48 rounded-full bg-primary/15 blur-3xl" />
                    {patientSelected ? (
                        <div className="relative p-5 sm:p-6">
                            {/* Eyebrow + price pill row */}
                            <div className="flex items-start justify-between gap-3 mb-3">
                                <div className="flex items-center gap-2 min-w-0">
                                    <span
                                        className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-[9.5px] font-bold uppercase tracking-[0.16em] shadow-sm ${cardBadgeClass(heroEyebrowColor)}`}
                                        data-testid="scan-source-io-eyebrow"
                                    >
                                        <Crown size={11} weight="fill" /> {heroEyebrow}
                                    </span>
                                    <span className="hidden sm:inline-flex items-center gap-1 text-[10.5px] font-semibold tracking-wider text-primary uppercase">
                                        <ShieldCheck size={11} weight="duotone" /> Free re-scan guarantee
                                    </span>
                                </div>
                                {ioScannerFee != null && (
                                    <div className="shrink-0 text-right" data-testid="scan-source-io-price">
                                        <div className="font-display text-[20px] sm:text-[22px] font-bold text-primary tabular-nums leading-none">
                                            {fmtINR(ioScannerFee)}
                                        </div>
                                        <div className="text-[10px] text-muted-foreground mt-0.5">per scan · invoiced with this case</div>
                                    </div>
                                )}
                            </div>

                            {/* Hero headline */}
                            <h3
                                className="font-display text-[20px] sm:text-[22px] font-bold text-foreground leading-tight tracking-tight"
                                data-testid="scan-source-io-headline"
                            >
                                {heroTitle}
                            </h3>
                            <p
                                className="text-[12.5px] text-foreground/75 mt-1.5 leading-snug max-w-2xl"
                                data-testid="scan-source-io-subtitle"
                            >
                                {heroSubtitle}
                            </p>

                            {/* Three value-prop tiles — driven by admin's promises[] */}
                            <div className="mt-4 grid grid-cols-1 sm:grid-cols-3 gap-2.5" data-testid="scan-source-io-value-tiles">
                                {heroPromises.slice(0, 3).map((p, i) => {
                                    const PIcon = resolveHeroIcon(p.icon);
                                    return (
                                        <div key={i} className="rounded-xl bg-white/85 backdrop-blur-sm border border-primary/20 px-3 py-3" data-testid={`scan-source-io-promise-${i}`}>
                                            <div className="flex items-center gap-2">
                                                <div className="w-8 h-8 rounded-lg bg-primary/10 text-primary grid place-items-center">
                                                    <PIcon size={16} weight="duotone" />
                                                </div>
                                                <div className="text-[11px] font-bold uppercase tracking-[0.12em] text-muted-foreground">{p.label}</div>
                                            </div>
                                            <div className="mt-1.5 font-display text-[15px] font-bold text-foreground leading-tight">
                                                {p.value || p.label}
                                            </div>
                                            {p.sub && (
                                                <p className="text-[11px] text-muted-foreground leading-snug mt-0.5">
                                                    {p.sub}
                                                </p>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>

                            {/* Inclusion checklist — driven by admin's features[] */}
                            <ul
                                className="mt-4 flex flex-wrap items-center gap-x-4 gap-y-1.5 text-[11.5px]"
                                data-testid="scan-source-io-inclusions"
                            >
                                {heroFeatures.map((f, i) => (
                                    <li key={i} className="flex items-center gap-1.5" data-testid={`scan-source-io-feature-${i}`}>
                                        <Check size={12} weight="bold" className="text-primary" />
                                        <span>
                                            <span className="font-semibold text-foreground">{f.label}</span>
                                            {f.value && (
                                                <span className="text-muted-foreground"> {f.value}</span>
                                            )}
                                        </span>
                                    </li>
                                ))}
                            </ul>

                            {/* CTA block — brand-primary "Book scan visit" CTA. */}
                            <div className="mt-5 pt-4 border-t border-primary/20 flex items-center justify-between gap-3 flex-wrap">
                                <div className="text-[11px] text-muted-foreground leading-snug max-w-md">
                                    <span className="font-semibold text-foreground">Scope:</span>{" "}
                                    records capture only — clinical exam, diagnosis &amp; patient counselling stay chair-side with you. Status flips to{" "}
                                    <span className="font-semibold text-foreground">Scan Visit Scheduled</span> once you pick a date &amp; time.
                                </div>
                                <Button
                                    size="lg"
                                    onClick={onBookScanVisit}
                                    className="bg-primary hover:bg-primary/90 text-primary-foreground gap-2 shrink-0 h-11 px-5 text-[13.5px] font-semibold shadow-md hover:shadow-lg transition-shadow"
                                    data-testid="scan-source-book-visit-btn"
                                >
                                    {heroCtaLabel}
                                    <CaretRight size={14} weight="bold" />
                                </Button>
                            </div>
                        </div>
                    ) : (
                        <div className="relative p-4 sm:p-5">
                            <div className="flex items-center justify-between gap-3 flex-wrap text-[12px]">
                                <div className="flex items-start gap-3 flex-1 min-w-0">
                                    <div className="shrink-0 w-9 h-9 rounded-lg bg-primary/10 text-primary grid place-items-center">
                                        <UserIcon size={17} weight="duotone" />
                                    </div>
                                    <div className="min-w-0">
                                        <div className="font-semibold text-foreground text-[13px]">
                                            Pick a patient to continue
                                        </div>
                                        <p className="text-muted-foreground mt-0.5 leading-snug">
                                            Select a patient from the field above first — we&apos;ll then show what&apos;s included in the visit{ioScannerFee != null ? ` (${fmtINR(ioScannerFee)} per scan)` : ""}.
                                        </p>
                                    </div>
                                </div>
                                <Button
                                    size="sm"
                                    disabled
                                    className="bg-primary/30 text-white gap-1.5 shrink-0 h-9 text-xs cursor-not-allowed"
                                    data-testid="scan-source-book-visit-btn"
                                >
                                    Book scan visit
                                    <CaretRight size={12} weight="bold" />
                                </Button>
                            </div>
                        </div>
                    )}
                </div>
                );
            })()}
        </div>
    );
}


/* RadioRailRow — iter97.92
 *
 * A single radio option for the inline visual rail used by
 * ScanSourcePicker. The visual grammar: an absolutely-positioned dot
 * sitting on the shared vertical rail, then the title + hint text laid
 * out to its right with generous padding. Selected = filled dot +
 * primary tint background + bolder title.
 */
/* ────────────────────────────────────────────────────────────────────
 * iter98.99g — Step 1 RECAP BANNER
 *
 * Renders at the top of Step 2 (Clinical Exam) so the doctor always
 * starts the next step with a compact, always-visible reminder of the
 * four choices they committed to in Step 1:
 *   01 Scan source · 02 Submission mode · 03 Compliance · 04 Mfg speed
 *
 * Each chip uses the same accent color the doctor saw on the Step 1
 * section header, so the visual identity carries through across steps.
 * A single "Edit setup" link routes back to Step 1 if anything needs
 * changing — same behaviour as click-Back, but more discoverable.
 * ──────────────────────────────────────────────────────────────────── */


export function RadioRailRow({ selected, onClick, title, hint, accent = "primary", tone = "soft", icon: IconOverride = null, testid, badge = "", badgeColor = "", bullets = [], deviceChip = null }) {
    /* iter98.99g — Section-identity refactor.
     *
     * Both Section 01 cards now share the section's hue (brand
     * primary teal `#007C9D` — same color as the "Book scan visit"
     * CTA, so the whole section reads as a single brand-cohesive
     * surface).
     *
     * Two cards differentiate by `tone`:
     *   - tone="soft" → barely-tinted card, light icon avatar
     *   - tone="bold" → slightly stronger wash, denser icon avatar
     * Selected state is identical for both tones — a soft gradient
     * background in the section accent + colored border — so when
     * either card is selected the section identity dominates.
     */
    const Icon = IconOverride || (tone === "bold" ? HouseLine : Cube);

    /* iter98.99ah/ai — "Settle" flash. Shared `useSettlePulse` hook
     * lives at ./hooks.js so every wizard picker (RadioRailRow,
     * ModeCard, LabChoiceCard, LabChip, IOScannerToggleCard) shares
     * the same 550ms scale + colored ring flash on the false→true
     * transition. Keeps "locking-in" feedback consistent across steps. */
    const flashCls = useSettlePulse(selected, "ring-primary/30");

    // Brand-primary family for Section 01. Uses Tailwind opacity
    // stops on the `primary` CSS variable so the section stays in
    // lockstep with any future brand-color updates.
    const families = {
        primary: {
            iconSel:    "bg-primary text-primary-foreground",
            iconSoft:   "bg-primary/10 text-primary",
            iconBold:   "bg-primary/[0.15] text-primary",
            titleSel:   "text-primary",
            borderSel:  "border-primary ring-2 ring-primary/25 bg-gradient-to-br from-primary/[0.10] via-white to-primary/[0.14]",
            borderSoft: "border-primary/15 bg-white hover:border-primary/40 hover:bg-primary/[0.04]",
            borderBold: "border-primary/25 bg-primary/[0.05] hover:border-primary/50 hover:bg-primary/[0.08]",
            check:      "bg-primary text-primary-foreground",
        },
    };
    const p = families[accent] || families.primary;
    const iconBg     = selected ? p.iconSel  : (tone === "bold" ? p.iconBold   : p.iconSoft);
    const borderCls  = selected ? p.borderSel : (tone === "bold" ? p.borderBold : p.borderSoft);
    const titleCls   = selected ? `font-semibold ${p.titleSel}` : "font-semibold text-foreground";

    return (
        <button
            type="button"
            onClick={onClick}
            className={`group relative w-full text-left rounded-xl border px-3.5 pt-5 pb-3 flex items-center gap-3 transition-all duration-300 ${borderCls} ${flashCls}`}
            data-testid={testid}
            aria-pressed={selected}
        >
            {/* iter98.99q → iter98.99r — Optional floating admin-set
                ribbon badge (top-right). Half-outside the card border,
                styled to match the canonical ModeCard / Tier / Compliance
                ribbon grammar (same font size, padding and tracking) so
                every wizard badge reads as one consistent visual family. */}
            {badge && (
                <span
                    className={`absolute -top-2.5 right-3 z-10 inline-flex items-center gap-1 px-3 py-[3px] rounded-full text-[10px] font-bold tracking-[0.12em] uppercase whitespace-nowrap shadow-md ring-1 ring-white/70 ${cardBadgeClass(badgeColor)}`}
                    data-testid={`${testid}-badge`}
                >
                    <Sparkle size={10} weight="fill" />
                    {badge}
                </span>
            )}
            <div className={`shrink-0 w-10 h-10 rounded-[10px] grid place-items-center transition-colors ${iconBg}`}>
                <Icon size={19} weight={selected ? "fill" : "duotone"} />
            </div>
            <div className="flex-1 min-w-0">
                <div className={`flex items-center gap-2 flex-wrap`}>
                    <span className={`text-[13px] leading-tight transition-colors ${titleCls}`}>
                        {title}
                    </span>
                    {selected && <SelectedPill accent={accent} testid={`${testid}-selected-pill`} />}
                </div>
                <div className="text-[11.5px] text-muted-foreground mt-0.5 leading-snug">
                    {hint}
                </div>
                {/* iter100.108 — Personalised device chip. Renders when the
                    doctor has declared an in-house scanner on their profile.
                    Sits outside the admin-editable title/subtitle so admin
                    copy overrides can't hide the doctor's own device name. */}
                {deviceChip && (
                    <div
                        className="mt-2 inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-primary/[0.08] border border-primary/20 text-primary text-[10.5px] font-semibold"
                        data-testid={`${testid}-device-chip`}
                    >
                        {deviceChip.icon && <deviceChip.icon size={11} weight="duotone" />}
                        {deviceChip.label}
                    </div>
                )}
                {/* iter98.99s — Admin-editable bullets (Pricing → Wizard
                    Cards → Scan Source). Rendered as a compact 1-col list
                    so the row stays slim. Hidden entirely when admin
                    hasn't added any bullets. */}
                {Array.isArray(bullets) && bullets.filter((b) => (b || "").trim()).length > 0 && (
                    <ul className="mt-2 space-y-1" data-testid={`${testid}-bullets`}>
                        {bullets.filter((b) => (b || "").trim()).slice(0, 4).map((b, i) => (
                            <li
                                key={i}
                                className="text-[11px] leading-snug text-foreground/80 flex items-start gap-1.5"
                                data-testid={`${testid}-bullet-${i}`}
                            >
                                <Check size={11} weight="bold" className="text-primary/80 shrink-0 mt-0.5" />
                                <span>{b}</span>
                            </li>
                        ))}
                    </ul>
                )}
            </div>
            {/* iter98.99s — Right-edge circular check removed; the
                inline SELECTED pill beside the title is now the single
                selection confirmation, matching every other wizard card. */}
        </button>
    );
}


/* iter98.99e1 — CoE-Support compliance opt-in card.
 *
 * Two-card yes/no picker that lives in Step 1, right after the Submission
 * mode picker. Question copy is locked: "Would you like Invisique's
 * Center of Excellence Support team to manage compliance for this case?"
 *
 * Design choices (intentional, locked with user):
 *   - No default — `value === null` until the doctor picks. The wizard's
 *     `canNext` gate blocks "Next" until value is boolean.
 *   - Teal accent on the YES card to mirror the brand-primary "engaged"
 *     state; neutral border on the NO card so it doesn't feel penalised.
 *   - Patient-gated: clicking before a patient is selected calls
 *     `onChange(null)` which the parent's `requirePatient` wrapper
 *     redirects to the "pick a patient first" toast.
 *   - Locked-at-submission is enforced server-side; this picker stays
 *     editable while in draft/revision_requested status.
 */
export function CoeSupportCompliancePicker({ value, onChange, patientSelected = true, fee = 0, cardSettings = null, hideCard = false }) {
    const picked = value === true || value === false;
    const isYes = value === true;
    const isNo  = value === false;
    /* iter100.113q — Fee label copy adapts to Yes vs No card. On No,
       there's nothing to bill so the footer just reads "Included · no
       add-on". Parity with Manufacturing Speed cards. */
    const feeLabel = fee > 0
        ? `₹${Number(fee).toLocaleString("en-IN")} per case · added to final invoice`
        : "Included free — no per-case charge";
    const feeMetaYes = fee > 0
        ? `+₹${Number(fee).toLocaleString("en-IN")} / case`
        : "Included";
    const feeMetaNo = "No add-on";

    /* iter98.99q — Admin-editable copy via Pricing → Wizard Cards →
     * Compliance Card. Each field falls back to the original hardcoded
     * copy when the admin hasn't overridden it. */
    const s = cardSettings || {};
    const sectionEyebrowAdmin = (s.section_eyebrow || "").trim();
    const sectionTitleAdmin   = (s.section_title || "").trim();
    const sectionBodyAdmin    = (s.section_body || "").trim();
    const yesTitleAdmin       = (s.yes_title || "").trim();
    const noTitleAdmin        = (s.no_title || "").trim();
    const yesBulletsAdmin     = Array.isArray(s.yes_bullets) && s.yes_bullets.length ? s.yes_bullets : null;
    const noBulletsAdmin      = Array.isArray(s.no_bullets) && s.no_bullets.length ? s.no_bullets : null;
    const yesBadge            = (s.badge || "").trim();
    const yesBadgeColor       = (s.badge_color || "rose").trim() || "rose";
    // iter98.99t — separate ribbon for the "No" card.
    const noBadge             = (s.no_badge || "").trim();
    const noBadgeColor        = (s.no_badge_color || "rose").trim() || "rose";

    const headerTitle = sectionEyebrowAdmin || "Compliance Monitoring & Support";
    const sectionQuestion = sectionTitleAdmin
        || "Would you like Invisique™'s Center of Excellence Support team to manage patient-side compliance after fitment?";
    const sectionBody = sectionBodyAdmin
        || "If yes, our Center of Excellence-Support team will reach out to the patient after aligner fitment to help them log wear-time, answer day-to-day questions, and proactively monitor compliance, alerting you immediately if a patient drifts off-track. You stay in full clinical control, we handle patient engagement.";
    const yesTitle = yesTitleAdmin || "Yes — Engage CoE Support";
    const noTitle  = noTitleAdmin  || "No — I'll handle compliance";
    const defaultYesBullets = [
        "Patient coached on wear-time logging from day 1",
        "Proactive drift monitoring — we escalate to you",
    ];
    const defaultNoBullets = [
        "Patient-side touchpoints stay with your clinic",
        "Wear-time + drift dashboards still available to you",
        "You can't change this after submitting",
    ];
    const yesBulletsList = yesBulletsAdmin || defaultYesBullets;
    const noBulletsList  = noBulletsAdmin  || defaultNoBullets;

    return (
        <div className={hideCard ? "" : "rounded-2xl border border-rose-100 bg-gradient-to-br from-rose-50/40 via-white to-rose-50/20 p-5 shadow-sm"} data-testid="step1-coe-support-card">
            <Step1SectionHeader
                num="03"
                icon={HeartStraight}
                title={headerTitle}
                helper="Let us handle patient engagement while you focus on patient care."
                accent="rose"
            />
            <div className="space-y-3 mt-3">
                <p className="text-sm text-foreground/85 leading-relaxed max-w-3xl">
                    <span className="font-medium" data-testid="coe-support-section-title">{sectionQuestion}</span>
                    <span className="block text-[12px] text-muted-foreground mt-1.5 leading-snug" data-testid="coe-support-section-body">
                        {sectionBody}
                    </span>
                </p>

                {/* iter100.113r — Social-proof stats strip. Two chips built
                    on the same rose+emerald palette as the cards below so
                    the whole section reads as one cohesive story. Copy is
                    the product default; both chips are hard-coded until we
                    ship a live analytics pipeline. */}
                <div className="flex flex-wrap items-center gap-2 pt-1" data-testid="coe-support-stats-strip">
                    <span
                        className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-rose-50 border border-rose-200 text-[11px] font-semibold text-rose-900"
                        data-testid="coe-support-stat-speed"
                    >
                        <Lightning size={11} weight="fill" className="text-rose-500" />
                        <span>
                            Cases with CoE support complete <strong className="tabular-nums">2.3&times;</strong> faster on average
                        </span>
                    </span>
                    <span
                        className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-emerald-50 border border-emerald-200 text-[11px] font-semibold text-emerald-900"
                        data-testid="coe-support-stat-completion"
                    >
                        <TrendUp size={11} weight="fill" className="text-emerald-600" />
                        <span>
                            <strong className="tabular-nums">87%</strong> higher case-completion rate with CoE support
                        </span>
                    </span>
                </div>

                {/* iter98.99s — pt-4 (was pt-1) gives the floating Yes
                    ribbon room to breathe between the body copy and the
                    cards. */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 pt-4">
                    <button
                        type="button"
                        role="radio"
                        aria-checked={isYes}
                        onClick={() => onChange(true)}
                        className={[
                            "text-left rounded-xl border-2 px-4 pt-6 pb-4 transition-all relative",
                            isYes
                                ? "border-rose-500 bg-gradient-to-br from-rose-100/80 via-rose-50 to-pink-50 ring-2 ring-rose-300/60 shadow-md"
                                : "border-rose-200 bg-rose-50/40 hover:border-rose-400 hover:bg-rose-50/70 hover:shadow-sm",
                        ].join(" ")}
                        data-testid="coe-support-yes"
                    >
                        {/* iter98.99r — Inner overflow-clipped wrapper so the
                            decorative rose glow stays inside the card rounding,
                            while the floating ribbon badge below can spill
                            half-outside the card edge (matches the Mode /
                            Tier / Scan-source ribbon grammar). */}
                        {isYes && (
                            <div aria-hidden="true" className="pointer-events-none absolute inset-0 rounded-xl overflow-hidden">
                                <div className="absolute -top-10 -right-10 w-32 h-32 rounded-full bg-rose-200/40 blur-2xl" />
                            </div>
                        )}
                        {/* iter98.99q → iter98.99r — Premium floating ribbon
                            badge: pill anchored just inside the top edge with
                            the upper half overflowing OUT of the card. Style
                            matches the ModeCard / Tier / IO ribbons. */}
                        {yesBadge && (
                            <span
                                className={`absolute -top-2.5 right-3 z-10 inline-flex items-center gap-1 px-3 py-[3px] rounded-full text-[10px] font-bold tracking-[0.12em] uppercase whitespace-nowrap shadow-md ring-1 ring-white/70 ${cardBadgeClass(yesBadgeColor, "rose")}`}
                                data-testid="coe-support-yes-badge"
                            >
                                <Sparkle size={10} weight="fill" />
                                {yesBadge}
                            </span>
                        )}
                        <div className="relative flex items-start gap-3">
                            {/* iter100.113r — Heart icon becomes a hover
                                explainer for doctors who are new to
                                Invisique's CoE service. Uses a CSS-only
                                group-hover pop-in so we don't need a
                                TooltipProvider wrapping the app. */}
                            <div className="group/heart relative shrink-0">
                                <div className={[
                                    "shrink-0 w-9 h-9 rounded-lg grid place-items-center transition-all shadow-sm cursor-help",
                                    isYes
                                        ? "bg-gradient-to-br from-rose-500 to-rose-600 text-white"
                                        : "bg-rose-100 text-rose-600",
                                ].join(" ")}
                                    aria-label="What is CoE — Center of Excellence support?"
                                    data-testid="coe-support-yes-icon"
                                >
                                    <HeartStraight size={18} weight={isYes ? "fill" : "duotone"} />
                                </div>
                                <div
                                    className="pointer-events-none absolute left-0 top-full mt-1.5 z-30 w-64 opacity-0 -translate-y-1 group-hover/heart:opacity-100 group-hover/heart:translate-y-0 transition-all duration-150"
                                    role="tooltip"
                                    data-testid="coe-support-heart-tooltip"
                                >
                                    <div className="rounded-lg bg-slate-900 text-white text-[11px] leading-snug px-3 py-2 shadow-xl border border-slate-700">
                                        <div className="font-bold text-rose-300 mb-0.5">CoE — Center of Excellence</div>
                                        Invisique&apos;s in-house clinical + care team that engages patients directly:
                                        wear-time coaching, drift alerts, day-to-day questions handled — you stay in
                                        clinical control while they manage patient side of things.
                                    </div>
                                </div>
                            </div>
                            <div className="min-w-0 flex-1">
                                <div className="flex items-center gap-2 flex-wrap">
                                    <span className={`font-semibold text-[14px] leading-tight ${isYes ? "text-rose-900" : "text-foreground"}`} data-testid="coe-support-yes-title">
                                        {yesTitle}
                                    </span>
                                    {isYes && <SelectedPill accent="rose" testid="coe-support-yes-selected-pill" />}
                                </div>
                                <ul className="mt-2 space-y-1 text-[11px] text-foreground/85 leading-snug" data-testid="coe-support-yes-bullets">
                                    {yesBulletsList.map((b, i) => (
                                        <li key={i} className="flex items-start gap-1.5" data-testid={`coe-support-yes-bullet-${i}`}>
                                            <CheckCircle size={11} weight="fill" className="text-emerald-600 shrink-0 mt-0.5" />
                                            {b}
                                        </li>
                                    ))}
                                </ul>
                                {/* iter100.113r — Trust badge — inline
                                    social-proof directly under the Yes
                                    bullets so it feels like an extension
                                    of the value list, not a marketing
                                    banner tacked on top. */}
                                <div
                                    className="mt-3 inline-flex items-start gap-1.5 px-2 py-1.5 rounded-md bg-rose-100/70 border border-rose-200 text-[10.5px] leading-snug text-rose-900 max-w-full"
                                    data-testid="coe-support-yes-trust-badge"
                                >
                                    <Sparkle size={11} weight="fill" className="text-rose-500 shrink-0 mt-0.5" />
                                    <span>
                                        <strong className="tabular-nums">87%</strong> of doctors who use CoE support see
                                        better case completion rates
                                    </span>
                                </div>
                            </div>
                        </div>
                        {/* iter100.113q — Add-on fee footer strip, mirroring
                            the Manufacturing Speed ModeCard meta grammar so
                            the fee is unmistakably visible instead of being
                            buried in the bullet list. */}
                        <div
                            className={`mt-3 -mx-4 -mb-4 px-4 py-2.5 border-t ${isYes ? "border-rose-300 bg-rose-100/60" : "border-rose-200/70 bg-rose-50/50"} rounded-b-[10px]`}
                            data-testid="coe-support-yes-fee-strip"
                        >
                            <div className="flex items-baseline justify-between gap-3">
                                <span className="font-bold uppercase tracking-wider text-muted-foreground text-[9px]">Add-on fee</span>
                                <span
                                    className={`font-display font-bold text-[13px] tabular-nums leading-tight ${fee > 0 ? "text-rose-900" : "text-emerald-800"}`}
                                    title={feeLabel}
                                    data-testid="coe-support-yes-fee-value"
                                >
                                    {feeMetaYes}
                                </span>
                            </div>
                        </div>
                    </button>

                    <button
                        type="button"
                        role="radio"
                        aria-checked={isNo}
                        onClick={() => onChange(false)}
                        className={[
                            "text-left rounded-xl border-2 px-4 pt-6 pb-4 transition-all relative",
                            isNo
                                ? "border-rose-400 bg-gradient-to-br from-rose-50 via-white to-pink-50 ring-2 ring-rose-200/60 shadow-md"
                                : "border-rose-100 bg-white hover:border-rose-300 hover:bg-rose-50/40 hover:shadow-sm",
                        ].join(" ")}
                        data-testid="coe-support-no"
                    >
                        {/* iter98.99r — Decorative rose glow lives inside its
                            own clipped wrapper so the parent button can stay
                            overflow-visible (badge ribbon parity with Yes). */}
                        {isNo && (
                            <div aria-hidden="true" className="pointer-events-none absolute inset-0 rounded-xl overflow-hidden">
                                <div className="absolute -top-10 -right-10 w-32 h-32 rounded-full bg-rose-200/40 blur-2xl" />
                            </div>
                        )}
                        {/* iter98.99t — Optional admin-set ribbon for the No card */}
                        {noBadge && (
                            <span
                                className={`absolute -top-2.5 right-3 z-10 inline-flex items-center gap-1 px-3 py-[3px] rounded-full text-[10px] font-bold tracking-[0.12em] uppercase whitespace-nowrap shadow-md ring-1 ring-white/70 ${cardBadgeClass(noBadgeColor, "rose")}`}
                                data-testid="coe-support-no-badge"
                            >
                                <Sparkle size={10} weight="fill" />
                                {noBadge}
                            </span>
                        )}
                        <div className="relative flex items-start gap-3">
                            <div className={[
                                "shrink-0 w-9 h-9 rounded-lg grid place-items-center transition-all shadow-sm",
                                isNo
                                    ? "bg-gradient-to-br from-rose-500 to-pink-500 text-white"
                                    : "bg-rose-50 text-rose-600",
                            ].join(" ")}>
                                <Stethoscope size={18} weight={isNo ? "fill" : "duotone"} />
                            </div>
                            <div className="min-w-0 flex-1">
                                <div className="flex items-center gap-2 flex-wrap">
                                    <span className={`font-semibold text-[14px] leading-tight ${isNo ? "text-rose-900" : "text-foreground"}`} data-testid="coe-support-no-title">
                                        {noTitle}
                                    </span>
                                    {isNo && <SelectedPill accent="rose" testid="coe-support-no-selected-pill" />}
                                </div>
                                <ul className="mt-2 space-y-1 text-[11px] text-foreground/85 leading-snug" data-testid="coe-support-no-bullets">
                                    {noBulletsList.map((b, i) => (
                                        <li key={i} className="flex items-start gap-1.5" data-testid={`coe-support-no-bullet-${i}`}>
                                            <CheckCircle size={11} weight="fill" className="text-rose-500 shrink-0 mt-0.5" />
                                            {b}
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        </div>
                        {/* iter100.113q — Matching Add-on fee footer strip for
                            visual parity with the Yes card. Nothing to bill
                            when the doctor declines the service. */}
                        <div
                            className={`mt-3 -mx-4 -mb-4 px-4 py-2.5 border-t ${isNo ? "border-rose-200 bg-rose-50/50" : "border-rose-100 bg-white/60"} rounded-b-[10px]`}
                            data-testid="coe-support-no-fee-strip"
                        >
                            <div className="flex items-baseline justify-between gap-3">
                                <span className="font-bold uppercase tracking-wider text-muted-foreground text-[9px]">Add-on fee</span>
                                <span
                                    className="font-display font-bold text-[13px] tabular-nums leading-tight text-emerald-800"
                                    data-testid="coe-support-no-fee-value"
                                >
                                    {feeMetaNo}
                                </span>
                            </div>
                        </div>
                    </button>
                </div>

                {/* iter98.99f5 — Warning banner removed per user direction.
                    Section 03 now behaves like every other Step 1 section:
                    the wizard's `canNext` gate alone enforces the pick.
                    Quieter UX, less visual noise on the page. */}
            </div>
        </div>
    );
}


export function PlanningModePicker({ mode, setMode, cards = null, wizardCopy = null, locked = false, lockedReason = "", hideHeader = false }) {
    /* iter86 (Wave C-lite) — value-prop comparison cards.
       iter92 — cards (admin-editable copy) override the hardcoded fallback.
       iter92.2 — wizardCopy also overrides the question + helper labels.
       iter98.56 — `locked` renders the chosen mode as a read-only summary
       so a doctor resuming a scan-visit case isn't asked to pick again
       (they already chose in Step 1 before booking the visit). */
    const copy = (k, fallback) => wizardCopy?.values?.[k] || fallback;
    /* iter100.113s — Eyebrow labels are branded vocabulary now
       ("Expert-Planned" / "Doctor-Planned"), so we hard-override any
       stale admin-configured eyebrow copy. Admin can still customize
       titles/bullets/best-for/timeline — only the eyebrow is locked. */
    const coe = {
        ...(cards?.coe || {}),
        eyebrow: "Expert-Planned",
        title: (cards?.coe?.title) || "Use Invisique™'s expert planning",
        bullets: (cards?.coe?.bullets && cards.coe.bullets.length ? cards.coe.bullets : [
            "You provide clinical data & imaging",
            "Our specialists author the per-tooth plan",
            "You review & approve before manufacturing",
        ]),
        best_for: (cards?.coe?.best_for) || "GPs · busy practices · complex cases",
        timeline: (cards?.coe?.timeline) || "≈ 3 weeks (1 wk planning + 2 wks mfg)",
        accent: "emerald",
        info_hint: (cards?.coe?.info_hint) || "Recommended for cases with 20+ aligners or complex movements",
    };
    const doc = {
        ...(cards?.doctor || {}),
        eyebrow: "Doctor-Planned",
        title: (cards?.doctor?.title) || "I'll plan the case myself",
        bullets: (cards?.doctor?.bullets && cards.doctor.bullets.length ? cards.doctor.bullets : [
            "You author the complete plan",
            "You specify movements & attachments",
            "We manufacture per your specifications",
        ]),
        best_for: (cards?.doctor?.best_for) || "Specialist orthos · simple cases · speed",
        timeline: (cards?.doctor?.timeline) || "≈ 2 weeks (mfg only, no planning step)",
        accent: "emerald",
    };

    /* Locked view — single read-only summary card. The doctor sees the
       choice they made before booking the scan visit, plus a quiet
       explainer of WHY it's locked. No way to change it inline (would
       require a fresh case). */
    if (locked && (mode === "coe" || mode === "doctor")) {
        const chosen = mode === "coe" ? coe : doc;
        return (
            <div data-testid="planning-mode-picker">
                <div className="mb-2 flex items-center gap-2">
                    <span className="text-[11px] font-bold tracking-[0.18em] uppercase text-muted-foreground">Submission mode</span>
                    <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider bg-amber-100 text-amber-800 rounded-full px-2 py-0.5">
                        <Lock size={9} weight="bold" /> Locked
                    </span>
                </div>
                <div
                    className="border border-primary/30 bg-primary/5 rounded-xl p-4"
                    data-testid={`planning-mode-locked-${mode}`}
                >
                    <div className="flex items-start gap-3">
                        <div className="w-10 h-10 rounded-lg bg-primary text-white grid place-items-center shrink-0">
                            {mode === "coe" ? <Lightning size={18} weight="duotone" /> : <UserGear size={18} weight="duotone" />}
                        </div>
                        <div className="flex-1 min-w-0">
                            <div className="text-[10px] font-bold uppercase tracking-wider text-primary">{chosen.eyebrow}</div>
                            <div className="font-display text-sm font-bold mt-0.5">{chosen.title}</div>
                            <p className="text-[11px] text-muted-foreground mt-2 leading-relaxed">
                                {lockedReason || "You committed to this mode when you booked the scan visit. It carries through the rest of this case."}
                            </p>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div data-testid="planning-mode-picker">
            {!hideHeader && (
                <div className="mb-2">
                    <span className="text-[11px] font-bold tracking-[0.18em] uppercase text-muted-foreground">Submission mode</span>
                    <span className="text-[12px] text-muted-foreground ml-2">Pick how you&apos;d like this case planned.</span>
                </div>
            )}
            {/* iter98.99s — pt-3 buffer between section header and the
                cards so the floating ribbon badges have breathing room. */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-3">
                <ModeCard
                    selected={mode === "coe"}
                    onClick={() => setMode("coe")}
                    eyebrow={coe.eyebrow}
                    title={coe.title}
                    subtitle={coe.subtitle}
                    badge={coe.badge}
                    badgeColor={coe.badge_color}
                    accent="emerald"
                    tone="soft"
                    bullets={coe.bullets || []}
                    meta={[
                        { k: "Best for", v: coe.best_for },
                        { k: "Timeline", v: coe.timeline },
                    ]}
                    infoHint={coe.info_hint || ""}
                    testid="planning-mode-coe"
                />
                <ModeCard
                    selected={mode === "doctor"}
                    onClick={() => setMode("doctor")}
                    eyebrow={doc.eyebrow}
                    title={doc.title}
                    subtitle={doc.subtitle}
                    badge={doc.badge}
                    badgeColor={doc.badge_color}
                    accent="emerald"
                    tone="bold"
                    bullets={doc.bullets || []}
                    meta={[
                        { k: "Best for", v: doc.best_for },
                        { k: "Timeline", v: doc.timeline },
                    ]}
                    testid="planning-mode-doctor"
                />
            </div>
        </div>
    );
}


export function ModeCard({ selected, onClick, eyebrow, title, subtitle = "", badge = "", accent, tone = "soft", bullets, meta, testid, icon: IconOverride = null, badgeColor = "", infoHint = "" }) {
    /* iter97.70 — Premium two-mode card. Restores the rich bullet list
       (was hidden behind a disclosure in iter97.69, which the user said
       made the picker feel "ordinary"). Bullets stay visible but the
       layout is denser: small icon-avatar at left, eyebrow + title at
       top, bullets in a compact 1-col stack, and the meta line ("Best
       for · Timeline") sitting as a tinted footer strip. Hover lift +
       brand-teal accent gradient on selected state keep the surface
       feeling premium without ballooning height. */
    /* iter98.99aj — Both unselected & selected states now carry a
       light accent wash so the cards feel "lived in" before the
       doctor picks one. Unselected uses a very faint ~3% accent
       tint; selected stays slightly stronger (5–10%) with the
       ring + border accent. The bg-card fallback on the root
       element was removed so this tint can render through.
       iter98.99g — Added `tone` prop ("soft" | "bold") so two
       ModeCards in the same section/accent can be subtly distinguished
       even before selection (two shades of the section's hue). Selected
       state remains tone-agnostic so the section identity (e.g. violet)
       dominates the picked card. */
    const accentRing = selected
        ? {
            primary: "ring-2 ring-primary bg-gradient-to-br from-primary/[0.06] to-primary/[0.12] border-primary shadow-sm",
            slate:   "ring-2 ring-slate-400 bg-gradient-to-br from-slate-50 to-slate-100 border-slate-400 shadow-sm",
            emerald: "ring-2 ring-emerald-400 bg-gradient-to-br from-emerald-50 via-white to-emerald-100/60 border-emerald-500 shadow-sm",
            amber:   "ring-2 ring-amber-500 bg-gradient-to-br from-amber-50 to-amber-100/60 border-amber-500 shadow-sm",
            violet:  "ring-2 ring-violet-400 bg-gradient-to-br from-violet-50 via-white to-indigo-50 border-violet-500 shadow-sm",
        }[accent || "primary"] || "ring-2 ring-primary bg-gradient-to-br from-primary/[0.06] to-primary/[0.12] border-primary shadow-sm"
        : (() => {
            // Per-accent unselected styles, split by tone so two cards in the
            // same section read as distinct (lighter vs slightly stronger
            // shade of the section's hue).
            const styles = {
                primary: {
                    soft: "border-border hover:border-primary/50 hover:shadow-md hover:-translate-y-0.5 bg-primary/[0.03]",
                    bold: "border-primary/20 hover:border-primary/60 hover:shadow-md hover:-translate-y-0.5 bg-primary/[0.06]",
                },
                slate: {
                    soft: "border-slate-100 hover:border-slate-300 hover:shadow-md hover:-translate-y-0.5 bg-slate-50/40",
                    bold: "border-slate-200 hover:border-slate-400 hover:shadow-md hover:-translate-y-0.5 bg-slate-100/40",
                },
                emerald: {
                    soft: "border-emerald-100 hover:border-emerald-300 hover:shadow-md hover:-translate-y-0.5 bg-emerald-50/40",
                    bold: "border-emerald-300 hover:border-emerald-500 hover:shadow-md hover:-translate-y-0.5 bg-emerald-100/50",
                },
                amber: {
                    soft: "border-amber-100 hover:border-amber-300 hover:shadow-md hover:-translate-y-0.5 bg-amber-50/40",
                    bold: "border-amber-200 hover:border-amber-400 hover:shadow-md hover:-translate-y-0.5 bg-amber-50/70",
                },
                violet: {
                    soft: "border-violet-100 hover:border-violet-300 hover:shadow-md hover:-translate-y-0.5 bg-violet-50/40",
                    bold: "border-indigo-200 hover:border-indigo-400 hover:shadow-md hover:-translate-y-0.5 bg-indigo-50/60",
                },
            };
            const s = styles[accent || "primary"] || styles.primary;
            return s[tone] || s.soft;
        })();
    const iconBg = {
        primary: selected ? "bg-primary text-white" : "bg-primary/10 text-primary",
        slate: selected ? "bg-slate-600 text-white" : "bg-slate-100 text-slate-700",
        emerald: (() => {
            if (selected) return "bg-gradient-to-br from-emerald-500 to-green-600 text-white";
            // iter100.113s — Boost bold-tone unselected icon opacity so
            // the right card doesn't feel like a downgrade next to the
            // left card. Was emerald-200/800 → now emerald-300/900 with a
            // ring for extra presence.
            return tone === "bold"
                ? "bg-emerald-300 text-emerald-900 ring-1 ring-emerald-400/50"
                : "bg-emerald-100 text-emerald-700";
        })(),
        amber: selected ? "bg-amber-500 text-white" : "bg-amber-100 text-amber-800",
        violet: (() => {
            if (selected) return "bg-gradient-to-br from-violet-500 to-indigo-500 text-white";
            return tone === "bold" ? "bg-indigo-100 text-indigo-700" : "bg-violet-100 text-violet-700";
        })(),
    }[accent || "primary"] || (selected ? "bg-primary text-white" : "bg-primary/10 text-primary");
    const badgeClass = (() => {
        // iter98.99r — Admin's `badge_color` (or per-tier `card_badge_color`)
        // wins when set. Falls back to the accent-derived default so existing
        // cards keep their look when the admin hasn't customised the pill.
        const bcOverride = {
            primary: "bg-primary text-white",
            slate:   "bg-slate-600 text-white",
            emerald: "bg-emerald-600 text-white",
            amber:   "bg-amber-500 text-white",
            violet:  "bg-violet-600 text-white",
            rose:    "bg-rose-600 text-white",
        }[(badgeColor || "").toLowerCase()];
        if (bcOverride) return bcOverride;
        return {
            primary: "bg-primary text-white",
            slate: "bg-slate-600 text-white",
            emerald: tone === "bold" ? "bg-emerald-700 text-white" : "bg-emerald-600 text-white",
            amber: "bg-amber-500 text-white",
            violet: tone === "bold" ? "bg-indigo-600 text-white" : "bg-violet-600 text-white",
        }[accent || "primary"] || "bg-primary text-white";
    })();
    /* iter98.99aa — Tick pill color follows the card's accent so the
       check looks like part of the same visual family as the icon
       tile + ring. */
    const selectedCheckClass = {
        primary: "bg-primary text-white",
        slate:   "bg-slate-600 text-white",
        emerald: "bg-gradient-to-br from-emerald-500 to-green-600 text-white",
        amber:   "bg-amber-500 text-white",
        violet:  "bg-gradient-to-br from-violet-500 to-indigo-500 text-white",
    }[accent || "primary"] || "bg-primary text-white";

    /* iter97.70 — Pick an icon that matches the mode. CoE = Crown
       (premium service); Self-plan = Sparkle (you author it). Callers
       can pass `icon` to override (used by the Manufacturing Speed
       picker which surfaces tier-specific glyphs). */
    const ModeIcon = IconOverride || (accent === "primary" ? Crown : Sparkle);

    /* iter98.99ah/ai — Shared settle-pulse across all wizard pickers.
       Accent-aware ring color so each card's pulse matches its family. */
    const flashRingAccent = {
        primary: "ring-primary/30",
        slate:   "ring-slate-400/30",
        emerald: "ring-emerald-400/35",
        amber:   "ring-amber-400/35",
        violet:  "ring-violet-400/35",
    }[accent || "primary"] || "ring-primary/30";
    const flashCls = useSettlePulse(selected, flashRingAccent);
    /* iter98.99ab — Meta footer is now fully data-driven. Each meta
       item gets its own column; items whose key contains "fee" /
       "price" / "charge" render with prominent (bold + tabular)
       styling so admin-set Manufacturing Speed pricing is obviously
       visible. Earlier the footer was hardcoded to "Best for /
       Timeline" only, which buried the fee. */
    const renderMetaItems = (meta || []).filter((m) => m && m.v);
    const isFeeKey = (k) => /fee|price|charge|cost/i.test(k || "");

    return (
        <button
            type="button"
            onClick={onClick}
            className={`relative rounded-xl border text-left p-3.5 pt-6 transition-all duration-300 flex flex-col h-full ${accentRing} ${flashCls}`}
            data-testid={testid}
            aria-pressed={selected}
        >
            {/* iter98.99p — Restore the original floating ribbon badge:
                pill anchored just inside the top edge with the upper
                half overflowing OUT of the card (the "halo" silhouette
                that gives the ModeCard its premium feel). The clipping
                bug from iter98.99z (which moved it inside) was caused
                by `overflow-hidden` interacting with the negative-margin
                footer strip — this is now resolved because the parent
                button has no overflow clipping, only `rounded-xl`. Sized
                up slightly (text-[10px], px-3, py-1, shadow-md) so the
                copy stays comfortably legible against any tone. */}
            {!!badge && (
                <span
                    className={`absolute -top-2.5 right-3 inline-flex items-center gap-1 px-3 py-[3px] rounded-full text-[10px] font-bold tracking-[0.12em] uppercase whitespace-nowrap shadow-md ring-1 ring-white/70 ${badgeClass} z-10`}
                    data-testid={`${testid}-badge`}
                >
                    {badge}
                </span>
            )}
            <div className="flex items-start gap-3">
                {/* iter98.99s — Tick overlay removed. The inline SELECTED
                    pill beside the title is now the single confirmation
                    indicator across every wizard card.
                    iter100.113s — `infoHint` prop turns this tile into a
                    hover-explainer for cards that need more context (e.g.
                    Expert-Planned's "20+ aligners" recommendation). Uses
                    CSS-only group-hover to avoid the TooltipProvider dep. */}
                <div className={`group/mode-icon shrink-0 ${infoHint ? "relative" : ""}`}>
                    <div className={`w-10 h-10 rounded-[10px] grid place-items-center transition-colors ${iconBg} ${infoHint ? "cursor-help" : ""}`}
                        aria-label={infoHint || undefined}
                    >
                        <ModeIcon size={20} weight={selected ? "fill" : "duotone"} />
                    </div>
                    {infoHint && (
                        <div
                            className="pointer-events-none absolute left-0 top-full mt-1.5 z-30 w-64 opacity-0 -translate-y-1 group-hover/mode-icon:opacity-100 group-hover/mode-icon:translate-y-0 transition-all duration-150"
                            role="tooltip"
                            data-testid={`${testid}-info-hint`}
                        >
                            <div className="rounded-lg bg-slate-900 text-white text-[11px] leading-snug px-3 py-2 shadow-xl border border-slate-700 flex items-start gap-1.5">
                                <Info size={12} weight="fill" className="text-emerald-300 shrink-0 mt-0.5" />
                                <span>{infoHint}</span>
                            </div>
                        </div>
                    )}
                </div>
                <div className="min-w-0 flex-1">
                    {!!eyebrow && (
                        <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-muted-foreground">
                            {eyebrow}
                        </div>
                    )}
                    <div className={`flex items-center gap-2 flex-wrap ${eyebrow ? "mt-0.5" : ""} pr-5`}>
                        <span className="font-semibold text-[14px] leading-tight">{title}</span>
                        {selected && <SelectedPill accent={accent || "primary"} testid={`${testid}-selected-pill`} />}
                    </div>
                    {!!subtitle && (
                        <div
                            className="text-[11px] text-muted-foreground mt-0.5 font-medium"
                            data-testid={`${testid}-subtitle`}
                        >
                            {subtitle}
                        </div>
                    )}
                    {bullets.length > 0 && (
                        <ul className="mt-3 mb-4 space-y-2.5" data-testid={`${testid}-bullets`}>
                            {bullets.slice(0, 3).map((b, i) => (
                                <li key={i} className="text-[11px] text-foreground/85 leading-snug flex gap-1.5">
                                    <Check size={11} weight="bold" className="text-primary/80 shrink-0 mt-0.5" />
                                    <span>{b}</span>
                                </li>
                            ))}
                        </ul>
                    )}
                </div>
            </div>
            {/* iter97.70 — Tinted footer strip rendering ALL meta items
                generically. Visually separates "what you get" (bullets
                above) from "who/when/how-much" metadata. Subtle so it
                doesn't compete with the title block above.
                iter98.99ab — Items whose key reads as a price/fee
                (e.g. "Add-on fee") render in a bigger, bolder, tabular
                style so admin-set Manufacturing Speed charges are
                obviously visible.
                iter98.99z → ad — `mt-auto` pins the footer to the
                bottom; the bullet list carries `mb-3` (instead of
                growing footer padding) so the breathing room sits
                ABOVE the divider where the user wanted it. */}
            {renderMetaItems.length > 0 && (
                <div className={`mt-auto -mx-3.5 -mb-3.5 px-3.5 py-2.5 border-t border-border/60 bg-secondary/30 grid gap-2 text-[10.5px] ${renderMetaItems.length >= 2 ? "grid-cols-2" : "grid-cols-1"}`}>
                    {renderMetaItems.map((m, i) => {
                        const isFee = isFeeKey(m.k);
                        const isTimeline = /timeline/i.test(m.k || "");
                        return (
                            <div key={i} className="min-w-0" data-testid={`${testid}-meta-${(m.k || "").toLowerCase().replace(/\s+/g, "-")}`}>
                                <div className="font-bold uppercase tracking-wider text-muted-foreground text-[9px]">
                                    {m.k}
                                </div>
                                <div
                                    className={
                                        isFee
                                            ? "font-display font-bold text-[13px] text-foreground tabular-nums mt-0.5 leading-tight"
                                            : "font-medium text-foreground/85 mt-0.5 leading-snug flex items-center gap-1"
                                    }
                                    title={m.v}
                                >
                                    {/* iter100.113s — Timeline meta row now
                                        carries a tiny clock icon for scannability
                                        at a glance. Skipped for fee/price rows so
                                        it doesn't dilute the price display. */}
                                    {isTimeline && !isFee && (
                                        <Clock size={11} weight="duotone" className="text-emerald-600 shrink-0" />
                                    )}
                                    <span className={isTimeline && !isFee ? "min-w-0 truncate" : ""}>{m.v}</span>
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}
        </button>
    );
}
